import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Logo from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public pricing page shown to the homeowner: persona summary at the top,
 * active pricing tiers below. Pay button is placeholder until payment gateway
 * is wired (admin can trigger simulate-paid from dashboard for now).
 */
export default function PricingPage() {
  const { id } = useParams();
  const [tiers, setTiers] = useState([]);
  const [portal, setPortal] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [tiersRes, portalRes] = await Promise.all([
          axios.get(`${API}/pricing/tiers`),
          axios.get(`${API}/portal/${id}`),
        ]);
        if (cancelled) return;
        setTiers(tiersRes.data || []);
        setPortal(portalRes.data || null);
      } catch (e) {
        toast.error("Couldn't load your rate card, please try again shortly.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const persona = portal?.persona_summary || "";
  const paid = portal?.advance_paid;

  return (
    <div style={{ minHeight: "100vh", background: "#FFFBC9", color: "var(--folk-text)" }} data-testid="pricing-page">
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "28px 48px 0" }}>
        <Logo variant="split" size={24} testId="pricing-brand-header" />
      </div>

      <div style={{ maxWidth: 900, margin: "0 auto", padding: "40px 24px 96px" }}>
        <div style={{ fontSize: 12, letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--folk-accent)", fontWeight: 600 }}>
          Your Folk report
        </div>
        <h1 className="folk-h1" data-testid="pricing-heading" style={{ maxWidth: 720, marginTop: 12 }}>
          your survey has been processed. here's what we <em style={{ fontStyle: "italic" }}>heard</em>.
        </h1>

        <div
          data-testid="persona-summary"
          style={{
            marginTop: 28, background: "#FFFFFF", border: "1.5px solid var(--folk-border)",
            borderRadius: 18, padding: "28px 28px 24px", whiteSpace: "pre-wrap",
            fontFamily: "'Playfair Display', serif", fontSize: 17, lineHeight: 1.65, color: "#1B1A1A",
          }}
        >
          {loading ? "Loading your report…" : (persona || "Your personalised report is being finalised — refresh in a moment.")}
        </div>

        {paid ? (
          <div
            data-testid="paid-banner"
            style={{
              marginTop: 40, background: "#BADDF0", border: "1.5px solid rgba(27,74,102,0.25)",
              borderRadius: 18, padding: "24px 28px", color: "#0F3A54",
            }}
          >
            <div style={{ fontWeight: 600, fontSize: 15 }}>🟢 Advance received — project is live.</div>
            <div style={{ marginTop: 6, fontSize: 14 }}>
              Your <b>{portal?.paid_tier?.name}</b> engagement is officially kicked off. Head over to your{" "}
              <Link to={`/portal/${id}`} style={{ color: "#0F3A54", fontWeight: 600 }}>portal</Link> to see the next steps.
            </div>
          </div>
        ) : (
          <>
            <h2 className="folk-h2" style={{ marginTop: 56 }}>ready to move forward? here's our <em style={{ fontStyle: "italic" }}>rate card</em>.</h2>
            <p className="folk-body" style={{ maxWidth: 620, marginTop: 8 }}>
              Once your advance is in, our team will launch your private Pinterest board and formally kick off the project.
            </p>

            <div data-testid="tier-grid" style={{ marginTop: 28, display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(300px, 100%), 1fr))", gap: 18 }}>
              {tiers.map((t) => (
                <div
                  key={t.id}
                  data-testid={`tier-${t.id}`}
                  style={{
                    background: t.is_recommended ? "#FFFFFF" : "#FAF5F3",
                    border: t.is_recommended ? "2px solid var(--folk-accent)" : "1.5px solid var(--folk-border)",
                    borderRadius: 18, padding: 24, display: "flex", flexDirection: "column", gap: 10,
                  }}
                >
                  {t.is_recommended && (
                    <div style={{ alignSelf: "flex-start", background: "#FFFBC9", color: "#68280E", fontSize: 11, letterSpacing: "0.14em", padding: "4px 12px", borderRadius: 999, textTransform: "uppercase", fontWeight: 600 }}>
                      Recommended
                    </div>
                  )}
                  <div className="folk-serif" style={{ fontSize: 24 }}>{t.name}</div>
                  <div style={{ color: "var(--folk-muted)", fontSize: 14, lineHeight: 1.5 }}>{t.description}</div>
                  <div style={{ fontSize: 20, color: "var(--folk-accent)", fontWeight: 600, marginTop: 6 }}>{t.price_display}</div>
                  <div style={{ fontSize: 13, color: "#2A2828" }}>Advance: <b>{t.advance_display}</b></div>
                  <ul style={{ margin: "10px 0 0 18px", padding: 0, fontSize: 13.5, color: "#2A2828", lineHeight: 1.65 }}>
                    {(t.features || []).map((f, i) => <li key={i}>{f}</li>)}
                  </ul>
                  <button
                    data-testid={`pay-btn-${t.id}`}
                    disabled
                    className="folk-pill"
                    style={{ marginTop: 14, opacity: 0.55, cursor: "not-allowed" }}
                    title="Payment gateway wiring pending — your Folk contact will send an invoice link."
                  >
                    Pay advance (Razorpay coming soon)
                  </button>
                </div>
              ))}
            </div>

            <p style={{ marginTop: 32, fontSize: 13.5, color: "var(--folk-muted)", lineHeight: 1.6 }}>
              For now, your Folk contact will share a secure invoice link directly. Once the advance is confirmed, this page will unlock and your project kicks off automatically.
            </p>
          </>
        )}

        <div style={{ marginTop: 48, fontSize: 13, color: "var(--folk-muted)" }}>
          <Link to={`/portal/${id}`} data-testid="back-to-portal" style={{ color: "#68280E", fontWeight: 600 }}>← Back to my portal</Link>
        </div>
      </div>
    </div>
  );
}
